// Student Editor - PartCPage10 (Post-inquiry reflections - editable student reflections with readonly teacher comments)
import React from 'react';
import { useActivityContext } from '../index';
import useAutoSave from '../../../hooks/useAutoSave';

const PartCPage10 = () => {
  const { activity } = useActivityContext();

  const teacherFields = activity?.part_c?.page_10?.teacher_fields || {};

  const studentFields = useAutoSave(
    activity.activity_id,
    10,
    'student',
    activity.my_response_part_c?.page_10 || {},
    'part-c'
  );

  return (
    <div style={{
      width: '100%',
      maxWidth: '1400px',
      minHeight: '80vh',
      margin: '0 auto',
      padding: '40px',
      backgroundColor: 'white',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      borderRadius: '8px',
      boxSizing: 'border-box',
      position: 'relative',
      fontFamily: "'Poppins', Arial, sans-serif"
    }}>
      {/* Save indicator */}
      {studentFields.saveStatus === 'saving' && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#FFC107',
          color: 'black',
          padding: '10px 20px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontSize: '14px'
        }}>
          Saving...
        </div>
      )}
      {studentFields.saveStatus === 'saved' && studentFields.lastSaved && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#4CAF50',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          zIndex: 1000,
          fontSize: '14px'
        }}>
          Saved ✓
        </div>
      )}

      {/* Top-right corner */}


      {/* Header */}
      <div style={{
        fontSize: '16px',
        fontWeight: 'bold',
        marginBottom: '15px',
        textAlign: 'center',
        backgroundColor: '#F4824C',
        color: 'white',
        padding: '10px'
      }}>
        Post-inquiry reflections
      </div>

      {/* Teacher Comments - Read Only */}
      <div style={{
        backgroundColor: '#FFE5D5',
        padding: '12px',
        marginBottom: '15px'
      }}>
        <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '8px' }}>
          Final comments by the teacher (if any):
        </div>
        <div style={{
          padding: '10px',
          minHeight: '100px',
          fontSize: '14px',
          lineHeight: '1.4',
          backgroundColor: '#FFE5D5',
          color: '#666'
        }}>
          {teacherFields.final_comments || 'No comments yet'}
        </div>
      </div>

      {/* Stick figure with speech bubble */}
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '15px', gap: '10px' }}>
        {/* Stick figure */}
        <svg width="50" height="70" viewBox="0 0 50 70" style={{ flexShrink: 0 }}>
          <circle cx="25" cy="12" r="10" stroke="black" strokeWidth="2" fill="white" />
          <line x1="25" y1="22" x2="25" y2="42" stroke="black" strokeWidth="2" />
          <line x1="25" y1="30" x2="12" y2="38" stroke="black" strokeWidth="2" />
          <line x1="25" y1="30" x2="38" y2="38" stroke="black" strokeWidth="2" />
          <line x1="25" y1="42" x2="15" y2="60" stroke="black" strokeWidth="2" />
          <line x1="25" y1="42" x2="35" y2="60" stroke="black" strokeWidth="2" />
        </svg>

        {/* Speech bubble */}
        <div style={{ position: 'relative', marginTop: '-10px' }}>
          <div style={{
            backgroundColor: 'white',
            border: '2px solid black',
            borderRadius: '10px',
            padding: '8px 12px',
            fontSize: '15px',
            lineHeight: '1.3',
            maxWidth: '120px',
            textAlign: 'center',
            fontWeight: 'bold'
          }}>
            Think about how you did in this research task.
            <div style={{
              position: 'absolute',
              bottom: '50%',
              left: '-10px',
              width: 0,
              height: 0,
              borderTop: '7px solid transparent',
              borderBottom: '7px solid transparent',
              borderRight: '10px solid black'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '50%',
              left: '-6px',
              width: 0,
              height: 0,
              borderTop: '5px solid transparent',
              borderBottom: '5px solid transparent',
              borderRight: '8px solid white'
            }} />
          </div>
        </div>
      </div>

      {/* Learner Reflection - Editable */}
      <div style={{
        backgroundColor: '#4A90A4',
        color: 'white',
        padding: '8px',
        fontWeight: 'bold',
        fontSize: '15px',
        textAlign: 'center',
        marginBottom: '10px'
      }}>
        Learner Reflection
      </div>

      {/* Reflection boxes with arrows */}
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', position: 'relative' }}>
          {[
            { label: 'What did I learn in this\nproblem-based inquiry?', key: 'what_learned' },
            { label: 'What was the most enjoyable\npart of the inquiry?', key: 'most_enjoyable' },
            { label: 'Three strengths I demonstrated\nin this inquiry', key: 'three_strengths' },
            { label: 'What was the least enjoyable\npart of the inquiry?', key: 'least_enjoyable' },
            { label: 'Three areas of improvement I identified\nthis inquiry', key: 'areas_improvement' },
            { label: 'Some questions I still have...', key: 'questions_still_have' }
          ].map((item, index) => (
            <div key={index} style={{
              backgroundColor: '#B3D9E8',
              padding: '10px',
              borderRadius: '4px',
              minHeight: '100px',
              position: 'relative'
            }}>
              <div style={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '6px', textAlign: 'center' }}>
                {item.label.split('\n').map((line, i) => <div key={i}>{line}</div>)}
              </div>
              <textarea
                value={studentFields.data[item.key] || ''}
                onChange={(e) => studentFields.updateData({ [item.key]: e.target.value })}
                placeholder={`Enter your reflection...`}
                maxLength={1000}
                style={{
                  width: '100%',
                  fontSize: '15px',
                  lineHeight: '1.3',
                  minHeight: '50px',
                  padding: '6px',
                  border: '1px solid #4A90A4',
                  borderRadius: '3px',
                  fontFamily: "'Poppins', Arial, sans-serif",
                  resize: 'vertical'
                }}
              />
            </div>
          ))}

          {/* Arrows */}
          <svg style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
          }}>
            <path d="M 10 130 Q 5 160, 10 190" stroke="#4A90A4" strokeWidth="3" fill="none" markerEnd="url(#arrowhead)" />
            <path d="M 10 310 Q 5 340, 10 370" stroke="#4A90A4" strokeWidth="3" fill="none" markerEnd="url(#arrowhead)" />
            <path d="M 380 130 Q 390 160, 380 190" stroke="#4A90A4" strokeWidth="3" fill="none" markerEnd="url(#arrowhead)" />
            <path d="M 380 310 Q 390 340, 380 370" stroke="#4A90A4" strokeWidth="3" fill="none" markerEnd="url(#arrowhead)" />

            <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="5" refY="5" orient="auto">
                <polygon points="0 0, 10 5, 0 10" fill="#4A90A4" />
              </marker>
            </defs>
          </svg>
        </div>

        {/* Last question full width */}
        <div style={{
          backgroundColor: '#B3D9E8',
          padding: '10px',
          borderRadius: '4px',
          minHeight: '80px',
          marginTop: '12px'
        }}>
          <div style={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '6px', textAlign: 'center' }}>
            How could your teacher modify this inquiry to make it more interesting?
          </div>
          <textarea
            value={studentFields.data.teacher_modify || ''}
            onChange={(e) => studentFields.updateData({ teacher_modify: e.target.value })}
            placeholder="Suggest improvements..."
            maxLength={1000}
            style={{
              width: '100%',
              fontSize: '15px',
              lineHeight: '1.3',
              minHeight: '40px',
              padding: '6px',
              border: '1px solid #4A90A4',
              borderRadius: '3px',
              fontFamily: "'Poppins', Arial, sans-serif",
              resize: 'vertical'
            }}
          />
        </div>
      </div>

      {/* Page Number */}
      <div style={{
        position: 'absolute',
        bottom: '15mm',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '14px',
        color: '#666',
        fontWeight: 'bold'
      }}>
        28
      </div>

      {/* Bottom corners */}


    </div>
  );
};

export default PartCPage10;
